/**
 * Day marks (My week, 3.2 stage 2): while a visit is selected or in hand, every
 * day header in both weeks says whether the visit could go there — a check for a
 * day that works, a caution for one where the move lands and raises the row as
 * an alert, nothing for a day already spent, a day without a shift, or the day
 * the visit is on now (MY-WEEK-INSPECT-PROMPT.md).
 *
 * The evaluation is the drag's own (`evaluateRealVisitMove`, or `evaluateMove`
 * for a reservation), run once per day; the caution text is a closed list chosen
 * by the guards' reason codes, never by matching their sentences. One check is
 * added for this build, a second visit of the same discipline group on the same
 * day; a PT visit and an SN visit on one day is fine.
 *
 * The patient's hours stay off the marks: the hour travels with the visit, so the
 * hours check answers the same on every day and cannot help choose one. My day
 * keeps it, and the Move anyway dialog still lists it.
 *
 * Nothing red, nothing blocks, nothing writes.
 */
import type { DateKey, DemoState, DisciplineGroup, PlanVisit } from "@caredna-marlin/utils/homeHealthDemo/state";
import { selectDemoNow, toDateKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { effectivePlacement, parseWindow } from "./constraints";
import { effectiveVisitResources } from "./effectiveCalendar";
import { evaluateMove, evaluateRealVisitMove, type GuardCode, type MoveEvaluation, type RealMoveEvaluation } from "./moveGuard";
import { disciplineForVisitTypeKind } from "./planProgress";
import { disciplineGroupFor } from "./roster";
import type { TimeSentence } from "@caredna-marlin/utils/homeHealthDemo/engine/timeText";

export type DayMarkKind = "works" | "caution" | "none";

export interface DayMark {
  date: DateKey;
  kind: DayMarkKind;
  /** Caution lines, from the closed list, one per reason. */
  reasons: string[];
  /** The note on a day that works: `Patient available AM only` or `… PM only`. */
  notes: string[];
}

export type MarkTarget = { realVisitId: number } | { planVisitId: string };

export const SECOND_VISIT = "Second visit that day";
export const PATIENT_NOT_AVAILABLE = "Patient not available";
export const RECERT_WINDOW = "Recert window";
export const VISIT_AT_RISK = "Visit at risk this period";

/**
 * The closed-list text for one guard code, or nothing when the code is not one
 * the marks speak to. Leaving the visit's week names the week the way the
 * popover does, and the count is the guard's own: "Week 1 drops to 1 of 2 SN
 * visits" (Pete, 2026-10-08), where "this week" read as the week on screen.
 */
export function closedReason(code: GuardCode, evaluation: Pick<RealMoveEvaluation, "checks">): string | undefined {
  switch (code) {
    case "patientDays":
      return PATIENT_NOT_AVAILABLE;
    case "outsideWeek":
    case "weekBehind":
      if (evaluation.checks.frequency && evaluation.checks.frequencyWeek && evaluation.checks.frequencyGroup)
        return `Week ${evaluation.checks.frequencyWeek} drops to ${evaluation.checks.frequency} ${evaluation.checks.frequencyGroup} visits`;
      return evaluation.checks.frequencyWeek ? `Moves out of week ${evaluation.checks.frequencyWeek}` : "Moves out of its week";
    case "recertWindow":
      return RECERT_WINDOW;
    case "periodThreshold":
      return VISIT_AT_RISK;
    default:
      return undefined;
  }
}

/** The caution lines a day mark shows: closed-list text only, each once. */
export function markReasons(evaluation: Pick<RealMoveEvaluation, "codes" | "checks">): string[] {
  const out: string[] = [];
  for (const code of evaluation.codes) {
    const text = closedReason(code, evaluation);
    if (text && !out.includes(text)) out.push(text);
  }
  return out;
}

/**
 * The reasons the **Move anyway** dialog lists on My week: the closed-list text
 * wherever a code has one, so the dialog reads exactly as the day's popper did,
 * and the guard's own sentence for the rest (the patient's hours, a language or
 * gender requirement), which the marks do not carry.
 */
export function dialogReasons(evaluation: Pick<RealMoveEvaluation, "codes" | "checks" | "reasons">): TimeSentence[] {
  const out: TimeSentence[] = [];
  const seen = new Set<string>();
  evaluation.reasons.forEach((reason, i) => {
    const code = evaluation.codes[i];
    const text = code ? closedReason(code, evaluation) : undefined;
    const key = text ?? JSON.stringify(reason);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(text ?? reason);
  });
  return out;
}

interface PatientVisit {
  date: DateKey;
  group: DisciplineGroup;
  realVisitId?: number;
  planVisitId?: string;
}

function groupOfVisitTypeId(state: Pick<DemoState, "meta" | "config">, visitTypeId: number): DisciplineGroup | undefined {
  const kind = Object.entries(state.meta.bindings.visitTypeIdsByKind).find(([, id]) => id === visitTypeId)?.[0];
  const discipline = kind ? disciplineForVisitTypeKind(state.config, kind as PlanVisit["visitTypeKind"]) : undefined;
  return discipline ? disciplineGroupFor(discipline) : undefined;
}

export function selectDayMarks(state: DemoState, target: MarkTarget, handle: string, dates: DateKey[], wallNow: Date = new Date()): Record<DateKey, DayMark> {
  const tz = state.config.branch.timeZone;
  const today = toDateKey(selectDemoNow(state, wallNow), tz);
  const resources = effectiveVisitResources(state);
  const none = (date: DateKey): DayMark => ({ date, kind: "none", reasons: [], notes: [] });
  const allNone = () => Object.fromEntries(dates.map((d) => [d, none(d)]));

  // --- the visit in hand, its plan visit, its patient ---------------------------
  const resource = "realVisitId" in target ? resources.find((v) => v.visitId === target.realVisitId) : undefined;
  const planVisit = "planVisitId" in target ? state.planVisits[target.planVisitId] : Object.values(state.planVisits).find((v) => v.realVisitId === target.realVisitId);
  if (!resource && !planVisit) return allNone();
  const episode = planVisit ? state.episodes[planVisit.episodeId] : undefined;
  const patientId = resource?.patientId ?? (episode ? state.meta.bindings.patientIdsByRef[episode.patientRef] : undefined);
  const patientRef = episode?.patientRef ?? (patientId !== undefined ? Object.entries(state.meta.bindings.patientIdsByRef).find(([, id]) => id === patientId)?.[0] : undefined);
  const patient = patientRef ? state.patients[patientRef] : undefined;
  const availability = (patient?.constraints ?? []).filter((c) => c.kind === "availabilityDays" || c.kind === "availabilityWindow");
  // No plan, no episode and nothing the patient told us: nothing to check, so
  // nothing is said. A visit no plan visit stands for, of a patient with an
  // episode, still has her other visits to count (L. Eastman, 2026-10-08).
  const hasEpisode = !!patientRef && Object.values(state.episodes).some((e) => e.patientRef === patientRef);
  if (!planVisit && availability.length === 0 && !hasEpisode) return allNone();

  const currentDate = resource ? toDateKey(new Date(resource.startIso), tz) : effectivePlacement(state, planVisit!).targetDate;
  const group = planVisit ? disciplineGroupFor(planVisit.discipline) : resource ? groupOfVisitTypeId(state, resource.visitTypeId) : undefined;

  // --- every other visit this patient has, real or planned, on the day it sits on
  // The plan's visits at the day each one sits on, then any tenant visit no plan
  // visit stands for — so a visit is counted once whichever side knows it.
  const others: PatientVisit[] = [];
  const planned = new Set<number>();
  if (episode) {
    for (const v of Object.values(state.planVisits)) {
      if (v.episodeId !== episode.id) continue;
      if (v.realVisitId !== undefined) planned.add(v.realVisitId);
      if (v.id === planVisit?.id || v.status === "canceled" || v.status === "missed") continue;
      others.push({ date: effectivePlacement(state, v).targetDate, group: disciplineGroupFor(v.discipline), planVisitId: v.id });
    }
  }
  if (patientId !== undefined) {
    for (const v of resources) {
      if (v.patientId !== patientId || v.eventStatus === "canceled" || v.visitId === resource?.visitId || planned.has(v.visitId)) continue;
      const g = groupOfVisitTypeId(state, v.visitTypeId);
      if (g) others.push({ date: toDateKey(new Date(v.startIso), tz), group: g, realVisitId: v.visitId });
    }
  }

  const providerId = state.meta.bindings.providerIdsByHandle[handle];
  const working = (date: DateKey) =>
    providerId !== undefined && state.server.shifts.some((s) => s.providerId === providerId && s.status === "confirmed" && !s.overflow && toDateKey(new Date(s.startIso), tz) === date);

  // The one note a day that works carries (Pete, 2026-10-08): the patient's
  // hours, when they sit all in the morning or all in the afternoon. The hours
  // never make a day amber — they are the same on every day — but they are what
  // she needs to know about the slot she is aiming at.
  const windowConstraint = availability.find((c) => c.kind === "availabilityWindow");
  const window = windowConstraint ? parseWindow(windowConstraint.value) : undefined;
  const hoursNote = window ? (window[1] <= 12 * 60 ? "Patient available AM only" : window[0] >= 12 * 60 ? "Patient available PM only" : undefined) : undefined;

  const out: Record<DateKey, DayMark> = {};
  for (const date of dates) {
    if (date < today || date === currentDate || !working(date)) {
      out[date] = none(date);
      continue;
    }
    const evaluation: Pick<RealMoveEvaluation, "codes" | "checks"> | undefined =
      resource !== undefined ? evaluateRealVisitMove(state, resource.visitId, date, undefined, wallNow) : (evaluateMove(state, planVisit!.id, date, undefined, wallNow) as MoveEvaluation | undefined);
    const reasons = evaluation ? markReasons(evaluation) : [];
    const sameDay = others.filter((o) => o.date === date);
    if (group && sameDay.some((o) => o.group === group)) reasons.push(SECOND_VISIT);
    if (reasons.length) {
      out[date] = { date, kind: "caution", reasons, notes: [] };
      continue;
    }
    out[date] = { date, kind: "works", reasons: [], notes: hoursNote ? [hoursNote] : [] };
  }
  return out;
}
